import React from "react";
import {
  Battery,
  Eye,
  Headphones,
  Radio,
  Video,
  Wifi,
  WifiOff,
  Zap,
} from "lucide-react";

import {
  ActionCard,
  InlineMessage,
  ModeHero,
  MonitorButton,
  MonitorPage,
  PageHeading,
  StatusLine,
} from "@/components/monitor/MonitorUI";
import styles from "@/pages/ParentModePage.module.scss";
import type { BabyDeviceInfo } from "./types";
import { getBabyDisplayName } from "./utils";
import { streamAction, streamingFirst } from "./monitorPresentation";

type DeviceSelectionViewProps = {
  babyDevices: BabyDeviceInfo[];
  selectedBaby: BabyDeviceInfo | null;
  isConnecting: boolean;
  error: string | null;
  onConnect: (baby: BabyDeviceInfo) => void;
  onOpenSupport: () => void;
};

function isStreaming(device: BabyDeviceInfo) {
  return device.cameraActive || device.micActive;
}

function streamDescription(device: BabyDeviceInfo) {
  if (device.cameraActive && device.micActive) return "Video + Audio";
  if (device.cameraActive) return "Video Only";
  if (device.micActive) return "Audio Only";
  return "Waiting for stream";
}

function DeviceMetadata({ device }: { device: BabyDeviceInfo }) {
  const metadata: React.ReactNode[] = [];
  if (device.batteryLevel != null) {
    metadata.push(
      <span key="battery">
        {device.isCharging ? (
          <Zap size={14} aria-hidden="true" />
        ) : (
          <Battery size={14} aria-hidden="true" />
        )}
        {device.batteryLevel}%{device.isCharging ? " · Charging" : ""}
      </span>,
    );
  }
  if (device.networkType) {
    const offline = device.networkType.toLowerCase() === "offline";
    metadata.push(
      <span key="network">
        {offline ? (
          <WifiOff size={14} aria-hidden="true" />
        ) : (
          <Wifi size={14} aria-hidden="true" />
        )}
        {device.networkType}
      </span>,
    );
  }

  if (metadata.length === 0) return null;
  return <div className={styles.deviceMetadata}>{metadata}</div>;
}

export const DeviceSelectionView: React.FC<DeviceSelectionViewProps> = ({
  babyDevices,
  selectedBaby,
  isConnecting,
  error,
  onConnect,
  onOpenSupport,
}) => {
  const sortedDevices = streamingFirst(babyDevices);

  if (babyDevices.length === 0) {
    return (
      <MonitorPage className={`${styles.page} ${styles.heroPage}`}>
        <div className={styles.heroArea}>
          <ModeHero
            tone="parent"
            icon={<Radio size={32} strokeWidth={1.8} />}
            status="Searching"
            title="Parent Mode"
            description="Looking for your baby device."
          />
        </div>
        {error ? <InlineMessage>{error}</InlineMessage> : null}
        <div className={styles.emptyActions}>
          <StatusLine>
            On the baby device, sign in to this account and open Baby Mode.
          </StatusLine>
          <StatusLine>
            Start Video + Audio or Audio Only, then keep that page open. Both
            devices need internet access.
          </StatusLine>
          <MonitorButton variant="quiet" onClick={onOpenSupport}>
            Need help?
          </MonitorButton>
        </div>
      </MonitorPage>
    );
  }

  if (babyDevices.length === 1) {
    const baby = babyDevices[0]!;
    const active = isStreaming(baby);
    const connecting = isConnecting && selectedBaby?.id === baby.id;
    const action = streamAction(baby);

    return (
      <MonitorPage className={`${styles.page} ${styles.heroPage}`}>
        <div className={styles.heroArea}>
          <ModeHero
            tone="parent"
            icon={
              baby.cameraActive ? (
                <Video size={32} strokeWidth={1.8} />
              ) : (
                <Eye size={32} strokeWidth={1.8} />
              )
            }
            status={active ? "Streaming" : "Online"}
            title={getBabyDisplayName(baby, 0)}
            description={streamDescription(baby)}
          />
          <DeviceMetadata device={baby} />
        </div>
        <div className={styles.primaryActions}>
          {error ? <InlineMessage>{error}</InlineMessage> : null}
          {!active && (
            <p id="waiting-for-stream" className={styles.waitingHint}>
              Start streaming on the baby device to continue.
            </p>
          )}
          <ActionCard
            aria-describedby={!active ? "waiting-for-stream" : undefined}
            icon={
              baby.cameraActive ? (
                <Video size={24} strokeWidth={2} />
              ) : (
                <Headphones size={24} strokeWidth={2} />
              )
            }
            label={connecting ? "Connecting…" : action}
            onClick={() => onConnect(baby)}
            disabled={!active || isConnecting}
            loading={connecting}
            loadingLabel="Connecting…"
          />
        </div>
      </MonitorPage>
    );
  }

  return (
    <MonitorPage className={styles.page}>
      <PageHeading
        title="Baby devices"
        description="Choose a baby device to watch or listen."
      />
      {error ? <InlineMessage>{error}</InlineMessage> : null}
      <div className={styles.deviceList}>
        {sortedDevices.map((baby, index) => {
          const active = isStreaming(baby);
          const connecting = isConnecting && selectedBaby?.id === baby.id;
          const action = streamAction(baby);
          return (
            <article className={styles.deviceRow} key={baby.id}>
              <div className={styles.deviceIcon} aria-hidden="true">
                {baby.cameraActive ? (
                  <Video size={24} />
                ) : baby.micActive ? (
                  <Headphones size={24} />
                ) : (
                  <Radio size={24} />
                )}
              </div>
              <div className={styles.deviceCopy}>
                <h2>{getBabyDisplayName(baby, index)}</h2>
                <p className={active ? styles.live : styles.standby}>
                  {active ? "Streaming" : "Online"} · {streamDescription(baby)}
                </p>
                <DeviceMetadata device={baby} />
              </div>
              <MonitorButton
                variant="primary"
                onClick={() => onConnect(baby)}
                disabled={!active || isConnecting}
                loading={connecting}
                loadingLabel="Connecting…"
              >
                {action}
              </MonitorButton>
            </article>
          );
        })}
      </div>
    </MonitorPage>
  );
};
